<template>
    <div ref="top">
        <ui-card class="pt-4 pl-4 pr-6">

            <p v-if="loading"><i class="fas fa-spin fa-spinner fa-fw mr-3"></i>Please wait. Loading application...</p>

            <div class="columns is-gapless mb-6" v-else>
                <div class="column is-narrow is-size-5"><i class="fad fa-fw fa-file-certificate fa-2x has-text-primary mr-5"></i></div>
                <div class="column">
                    <div class="has-text-weight-bold is-size-5 mt-1">{{ application.title }}</div>
                    <div class="is-size-6 has-text-grey">{{ application.cert.certification.title }}</div>

                    <!-- in progress -->
                    <section v-if="regApplication.progress <= application.outline.length && !regApplication.completed_at && !regApplication.is_in_review && !payment">
                        <div class="is-size-6 mt-3"><strong>Step {{ regApplication.progress }} of {{ lastStepCount }}</strong>: {{ component.title }}</div>

                        <b-message type="is-warning" v-if="regApplication.rejection_message" class="mt-5">
                            <div class="columns is-gapless">
                                <div class="column is-narrow">
                                    <i class="far fa-fw fa-exclamation-triangle mr-2"></i>
                                </div>
                                <div class="column">
                                    <div class="mb-3 has-text-weight-bold">There is a Problem with Your Application</div>
                                    <div>{{ regApplication.rejection_message }}</div>
                                </div>
                            </div>
                        </b-message>

                        <hr class="mt-5 mb-6">



                        <render-blocks :blocks="content" :data="regApplication.data" :enrollment="registration" :component="component" @saveData="saveData"></render-blocks>

                        <hr class="mt-6">

                        <section v-if="component && component.content && component.content.attributes.require_signature === '1'">
                            <signature :user="currentUser" :component="component" @input="saveSig"></signature>
                        </section>

                        <div class="mt-6">
                            <b-button @click="submit" size="is-large" type="is-info" expanded :disabled="isNextDisabled">Next Step<i class="fal fa-arrow-right-long ml-3"></i></b-button>
                        </div>
                    </section>

                    <!-- in review -->
                    <section v-else-if="regApplication.is_in_review">
                        <div class="is-size-6 mt-3"><strong>Step {{ regApplication.progress }} of {{ lastStepCount }}</strong>: {{ component.title }} <strong class="has-text-warning">(In Review)</strong></div>
                        <hr class="mt-5 mb-6">
                        <b-message type="is-success">
                            <p>The step you just completed in your application requires approval to continue. During this time your information will be reviewed and checked for accuracy. This process can take up to 48 hours. You will receive an email to {{ currentUser.email }} when your application has been processed and you can continue the application process.</p>
                            <div class="mt-4 pr-6 has-text-weight-bold">
                                <i class="far fa-fw fa-arrow-right mr-2"></i>Please, check back later!
                            </div>
                        </b-message>
                    </section>

                    <!-- payment -->
                    <section v-else-if="payment">
                        <div class="is-size-6 mt-3"><strong>Step {{ regApplication.progress }} of {{ lastStepCount }}</strong>: Payment</div>
                        <hr class="mt-5 mb-6">

                        <b-message type="is-success">
                            <p>Your application has been approved and is complete. A fee is required to award this certification. Please enter your card information below to continue.</p>
                        </b-message>

                        <div>
                            <h4 class="mt-6 mb-4">Access/Discount Code</h4>

                            <div class="columns" v-if="!discount.id">
                                <div class="column">
                                    <b-field message="Optional">
                                        <b-input v-model="discountCode"/>
                                    </b-field>
                                </div>
                                <div class="column">
                                    <b-button type="is-info" @click="applyDiscount">Apply Code</b-button>
                                </div>
                            </div>

                            <div class="columns" v-else>
                                <div class="column">
                                    <b-message type="is-success" variant="success" v-if="discount.type === 'amount'">
                                        <i class="fas fa-check-circle mr-2"></i>{{ discount.title }}: {{ discount.value | currency }} Discount
                                        <b-button size="sm" variant="success" style="float: right;" @click="removeDiscount">Remove</b-button>
                                    </b-message>
                                    <b-message type="is-success" v-else-if="discount.type === 'percent'">
                                        <i class="fas fa-check-circle mr-2"></i>{{ discount.title }}: {{ discount.value.toFixed(2) }}% Discount
                                        <b-button size="sm" variant="success" style="float: right;" @click="removeDiscount">Remove</b-button>
                                    </b-message>
                                </div>
                            </div>

                            <div class="columns mt-2" v-if="discount.error">
                                <div class="column">
                                    <b-message type="is-warning"><i class="fas fa-exclamation-circle mr-2"></i>{{ discount.error }}</b-message>
                                </div>
                            </div>
                        </div>

                        <div v-if="chargeAmount > 0">
                            <b-message type="is-warning" class="mt-6">
                                <div class="pr-6">
                                    <i class="far fa-fw fa-arrow-right mr-2"></i>Your card will be charged <strong>{{ chargeAmount | currency }}</strong>.
                                </div>
                            </b-message>

                            <!-- Tax breakdown -->
                            <div v-if="taxAmount > 0" class="mt-4 mb-4" style="background-color: #f8f9fa; padding: 1rem; border-radius: 4px; border: 1px solid #e0e2e8;">
                                <table style="width: 100%; font-size: 0.95rem; border-collapse: collapse;">
                                    <tr>
                                        <td style="padding-bottom: 0.25rem; border: none;">Application Fee:</td>
                                        <td style="text-align: right; padding-bottom: 0.25rem; border: none;">{{ baseAmount | currency }}</td>
                                    </tr>
                                    <tr v-if="taxDetails">
                                        <td style="padding-bottom: 0.25rem; border: none;">Sales Tax ({{ (taxDetails.taxRate * 100).toFixed(2) }}%, {{ taxDetails.jurisdiction }}):</td>
                                        <td style="text-align: right; padding-bottom: 0.25rem; border: none;">{{ taxAmount | currency }}</td>
                                    </tr>
                                    <tr v-else>
                                        <td style="padding-bottom: 0.25rem; border: none;">Sales Tax:</td>
                                        <td style="text-align: right; padding-bottom: 0.25rem; border: none;">{{ taxAmount | currency }}</td>
                                    </tr>
                                    <tr style="font-weight: 600;">
                                        <td style="padding-top: 0.5rem; border: none; border-top: 1px solid #dee2e6;">Total:</td>
                                        <td style="text-align: right; padding-top: 0.5rem; border: none; border-top: 1px solid #dee2e6;">{{ chargeAmount | currency }}</td>
                                    </tr>
                                </table>
                            </div>

                            <div v-if="!clientSecret" class="pb-3"><i class="fas fa-spin fa-spinner fa-fw mr-3"></i>Establishing secure connection for Credit Card...</div>

                            <div v-show="clientSecret" class="card-container">
                                <div ref="card"></div>
                                <b-message type="is-danger" v-show="stripeErrors" class="mt-6">
                                    <div id="card-errors"><i class="far fa-exclamation-triangle mr-3"></i>{{ stripeErrors }}</div>
                                </b-message>
                            </div>

                            <div class="mt-6">
                                <b-button @click="runPayment" size="is-large" type="is-info" expanded :disabled="!ccReady || processing"><strong>Pay {{ chargeAmount | currency }}</strong> & Continue<i class="fal fa-arrow-right-long ml-3"></i></b-button>
                            </div>
                        </div>
                        <div class="mt-6" v-else>
                            <b-button @click="runPayment" size="is-large" type="is-info" expanded>Next Step<i class="fal fa-arrow-right-long ml-3"></i></b-button>
                        </div>

                    </section>

                    <!-- complete -->
                    <section v-else>
                        <div class="is-size-6 mt-3"><strong>Step {{ lastStepCount }} of {{ lastStepCount }}</strong>: Complete</div>
                        <hr class="mt-5 mb-6">

                        <div v-for="award in application.cert.awards" class="award">

                            <div class="columns is-gapless">
                                <div class="column is-narrow"><i class="fad fa-fw fa-certificate fa-2x has-text-primary mr-3"></i></div>
                                <div class="column">
                                    <div class="has-text-weight-bold is-size-5">{{ award.title }}</div>
                                    <div class="mt-2">{{ award.description }}</div>
                                    <div class="mt-3 has-text-grey is-size-7">
                                        <div><strong class="">Approved: </strong>&nbsp;{{ regApplication.completed_at | moment('MMMM Do, YYYY') }}</div>
                                        <div><strong>Provider: </strong>&nbsp;{{ application.organization.title }}</div>
                                        <div v-if="application.cert.certification.type === 'unit'"><strong>{{ application.cert.certification.unit_title }}:</strong> {{ regApplication.total_units }}</div>
                                    </div>

                                    <template v-if="award.type === 'PDF'">
                                        <hr class="mt-5">

                                        <div class="mt-6">
                                            <b-button
                                                tag="a"
                                                target="_blank"
                                                size="is-large"
                                                type="is-info"
                                                expanded
                                                :href="apiUrl + 'app/registrations/' + registration.id + '/applications/' + applicationId + '/pdf/' + award.key"
                                            ><i class="fas fa-file-pdf mr-4"></i>{{ award.pdf_title }}
                                            </b-button>
                                        </div>
                                    </template>
                                    <template v-else-if="award.type === 'CERT'">

                                    </template>
                                </div>
                            </div>

                            <hr class="mt-6 mb-6 award-divider">

                        </div>
                    </section>


                    <!--Component-->
                    <!--<json-viewer :value="component.content.attributes"></json-viewer>-->

                    <!--Registration-->
                    <!--<json-viewer :value="registration"></json-viewer>-->

                    <!--Application-->
                    <!--<json-viewer :value="application"></json-viewer>-->

                    <!--Registration Application-->
                    <!--<json-viewer :value="regApplication"></json-viewer>-->
                </div>
            </div>


        </ui-card>
    </div>
</template>

<script>
    import {client as http, apiUrl, stripePK, verifySession} from "../../../http_client";
    import async from "async";
    import UiCard from "../../../components/UiCard.vue";

    import LessonComponent from "../../../components/course/LessonComponent";
    import QuizComponent from "../../../components/course/QuizComponent";
    import SurveyComponent from "../../../components/course/SurveyComponent";
    import CheckpointComponent from "../../../components/course/CheckpointComponent";
    import RenderBlocks from "../../../components/course/RenderBlocks.vue";
    import Signature from "./Signature.vue";
    import {mapGetters} from "vuex";

    let stripe = Stripe(stripePK),
        elements = stripe.elements(),
        card = undefined;

    export default {
        components: {RenderBlocks, UiCard, LessonComponent, QuizComponent, CheckpointComponent, SurveyComponent, Signature},
        props: ['registrationId', 'applicationId'],
        data: function() {
            return {
                registration: {},
                regApplication: {},
                confApplication: {},
                application: {},
                loading: true,
                apiUrl,

                lastStepCount: 0,

                signature: null,
                discountCode: null,
                discount: { // server return
                    error: null
                },
                payment: false,
                stripeErrors: null, // stripe errors
                stripeInitialized: false,
                clientSecret: null,
                intentId: null,
                intentCharge: 0,
                ccReady: false,
                processing: false,
                taxAmount: 0,
                baseAmount: 0,
                taxDetails: null
            };
        },
        computed: {
            ...mapGetters(['currentUser']),
            component: function() {
                if (this.application.id) {
                    let component = this.application.outline[this.regApplication.progress - 1].component;
                    if (typeof component.content === 'string') component.content = JSON.parse(component.content);
                    return component;
                } else {
                    return null;
                }
            },
            content: function() {
                // TODO this is conflicting with the 'component' computed property so we have to see if its been parsed or not
                if (this.application.id) {
                    let component = this.application.outline[this.regApplication.progress - 1].component;
                    if (typeof component.content === 'string') {
                        return component.content = JSON.parse(component.content).content;
                    } else {
                        return component.content.content;
                    }
                } else {
                    return [];
                }
            },
            isNextDisabled: function() {
                if (this.component.content.attributes.require_signature === '1') {
                    return !this.signature;
                } else {
                    return false;
                }
            },
            chargeAmount() {
                let charge = this.regApplication.quoted_price || 0;

                if (this.discount.value) {
                    if (this.discount.type === 'percent') {
                        charge = charge - Math.floor(charge * (this.discount.value / 100));
                    } else {
                        charge = Math.max(0, charge - this.discount.value);
                    }
                }

                // Add tax amount
                if (this.taxAmount) {
                    charge += this.taxAmount;
                }

                return charge;
            }
        },
        methods: {
            load: function(force) {
                this.loading = true;
                async.series([
                    (cb) => {
                        http.get('/app/registrations/' + this.registrationId, {force}).then(response => {
                            this.registration = response.data;
                            cb();
                        }).catch(err => {
                            console.log('registration error', err);
                            cb(err);
                        });
                    },
                    (cb) => {
                        http.get('/app/registrations/' + this.registrationId + '/applications/' + this.applicationId, {force}).then(response => {
                            this.regApplication = response.data;
                            this.registration.regApplication = this.regApplication;
                            cb();
                        }).catch(err => {
                            console.log('regApplication error', err);
                            cb(err);
                        });
                    },
                    (cb) => {
                        for (let i = 0; i < this.registration.conference.applications.length; i++) {
                            const application = this.registration.conference.applications[i];
                            if (application.id === this.regApplication.conf_application_id) {
                                this.confApplication = application;
                                this.registration.confApplication = this.confApplication;
                                return cb();
                            }
                        }
                        return cb('no match');
                    },
                    (cb) => {
                        http.get('/app/applications/' + this.confApplication.application_id, {force}).then(response => {
                            this.application = response.data;
                            this.registration.application = this.application;
                            this.lastStepCount = this.application.outline.length + 1;
                            if (this.regApplication.quoted_price) this.lastStepCount++;

                            if (this.regApplication.progress > this.application.outline.length && this.regApplication.is_unpaid) {
                                console.log('payment');
                                this.payment = true;
                                this.getPaymentIntent();
                            }
                            cb();
                        }).catch(err => {
                            cb(err);
                        });
                    },
                ], err => {
                    if (err) {
                        console.log(err);
                    }

                    this.loading = false;
                });
            },
            submit: function() {
                if (!this.loading) {
                    this.loading = true;
                    let payload = {};

                    if (this.discount.id) {
                        payload.discount_id = this.discount.id;
                        payload.total_paid = this.chargeAmount;
                    }

                    http.post('/app/registrations/' + this.registrationId + '/applications/' + this.applicationId + '/progress', payload, {force: true}).then(response => {

                        console.log(this.regApplication.progress, this.lastStepCount);

                        this.scrollTo('top');
                        if (this.component.content.attributes.require_approval === '1') {
                            console.log('in review');
                            this.regApplication.is_in_review = 1;
                        } else if (this.regApplication.progress === this.lastStepCount) {
                            console.log('complete');
                            this.regApplication.completed_at = new Date();
                        } else {
                            console.log('progress');
                            this.regApplication.progress++;
                        }

                        if (this.regApplication.progress > this.application.outline.length && this.regApplication.is_unpaid) {
                            console.log('payment');
                            this.payment = true;
                            this.getPaymentIntent();
                        }

                        this.loading = false;
                    }).catch(err => {
                        this.loading = false;
                        console.log(err);
                    });
                }
            },
            saveData: function(payload) {
                payload.reg_application_id = parseInt(this.applicationId);

                // TODO this is bit of a workaround
                try {
                    JSON.parse(payload.value);
                } catch (e) {
                    payload.value = JSON.stringify(payload.value);
                }

                console.log('saveData', payload);

                http.post('/app/registrations/' + this.registrationId + '/applications/' + this.applicationId + '/save', payload, {force: true}).then(response => {

                }).catch(err => {

                });
            },
            saveSig: function(data) {
                this.signature = data;
                this.saveData({
                    field: 'signature',
                    block: JSON.stringify(this.component.content.attributes),
                    value: JSON.stringify({signature: data})
                });
            },
            applyDiscount: function() {
                this.discount = {error: null};

                http.get('app/conferences/discount/' + this.registration.conference.code + '/' + this.discountCode + '/application').then(response => {
                    this.discount = response.data;
                    this.taxAmount = 0;
                    this.baseAmount = 0;
                    this.taxDetails = null;
                    this.recalcIntent();
                }).catch(err => {
                    this.discount.error = err.response.data;
                });
            },
            removeDiscount: function() {
                this.discountCode = null;
                this.discount = {code: null, error: null};
                this.taxAmount = 0;
                this.baseAmount = 0;
                this.taxDetails = null;
                this.recalcIntent();
            },
            getPaymentIntent: function() {
                let intentPayload = {id: this.applicationId};
                if (this.discount.id) {
                    intentPayload.discount_id = this.discount.id;
                }
                http.post('app/registrations/' + this.registration.id + '/applications/intent', intentPayload, {force: true}).then(response => {
                    this.intentId = response.data.id;
                    this.clientSecret = response.data.clientSecret;
                    this.baseAmount = response.data.baseAmount || 0;
                    this.taxAmount = response.data.taxAmount || 0;
                    this.taxDetails = response.data.taxDetails || null;

                    card = elements.create('card');
                    card.mount(this.$refs.card);

                    card.on('change', event => {
                        if (event.error) {
                            this.stripeErrors = event.error.message;
                        } else {
                            this.stripeErrors = '';
                        }

                        this.ccReady = !!(event.complete);
                    });
                }).catch(err => {
                    window.alert("I'm sorry, there was an error establishing a connection with the payment processor. Please try again later. If you continue to have this issue please contact support.");
                });
            },
            recalcIntent() {
                if (this.chargeAmount > 0) {
                    this.intentId = null;
                    this.clientSecret = null;
                    this.getPaymentIntent();
                }
            },
            runPayment() {
                if (this.chargeAmount > 0) {
                    if (!this.processing) {
                        this.processing = true;

                        // Confirm the session is still alive BEFORE the card is charged —
                        // a tab left open past the JWT lifetime can still confirm the
                        // payment with Stripe but then 401 on the submit call below
                        verifySession().then(() => {
                            stripe.confirmCardPayment(this.clientSecret, {
                                payment_method: {
                                    card: card,
                                    billing_details: {
                                        name: this.currentUser.first_name + ' ' + this.currentUser.last_name,
                                        email: this.currentUser.email
                                    }
                                }
                            }).then(result => {
                                if (result.error) {
                                    this.stripeErrors = result.error.message;
                                    console.log(result.error.message);
                                    this.processing = false;
                                } else if (result.paymentIntent.status === 'succeeded') {
                                    this.regApplication.progress++;
                                    this.regApplication.is_unpaid = 0;
                                    this.regApplication.total_paid = this.chargeAmount;
                                    this.payment = false;
                                    this.submit();
                                }
                            });
                        }).catch(err => {
                            this.processing = false;
                            if (err.response && err.response.status === 401) {
                                window.alert('Your session has expired. Please sign in and try again. Your card has NOT been charged.');
                            }
                        });
                    }
                } else {
                    this.regApplication.progress++;
                    this.regApplication.is_unpaid = 0;
                    this.regApplication.total_paid = this.chargeAmount;
                    this.payment = false;
                    this.submit();
                }
            },
            scrollTo(refName) {
                let element = this.$refs[refName];
                if (element) {
                    element.scrollIntoView({behavior: 'smooth'});
                }
            },
        },
        mounted() {
            this.load(true);
        },
        destroyed() {
            if (card) {
                card.destroy();
            }
        }
    };
</script>

<style lang="scss" scoped>
    .award:last-child .award-divider {
        display: none;
    }

    .card-container {
        border: 1px solid #e7e7e7;
        border-radius: 8px;
        padding: 2rem;
        margin-bottom: 2rem;
    }

    .errors-container {
        border: 2px solid red;
        border-radius: 8px;
        padding: 1rem 2rem;
        margin-top: 2rem;
    }
</style>

<style lang="scss">
    .block > div, .render-paragraph, .render-header, .field {
        padding-left: 0 !important;
    }
</style>
